import { Col, Row, Sidepanel, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import {
  BONUS_ELIGIBILITY,
  CAP_BEHAVIOR,
  FANWAVE_MODE,
  gameDayTimingShape,
  matchMinutesLabel,
  minutesToHoursLabel,
  missingTapTargetReason,
  REWARDS_SOURCE,
  scheduleUnrecorded,
  tapQuotaEnabled,
  WAVE_STATUS,
  WAVE_TIMING
} from '../constants';
import { HALF_TIME_WAVE_MIN } from '../anchors';
import { Badge, PanelHeader, ResolvedTarget, STATUS_BADGE_VARIANT } from './ui';

/**
 * Read-only configuration drill-down for one wave (opened by clicking a wave-list row).
 *
 * A wave carries different config depending on where it lives (see GET /admin/fanwave/list):
 *   - scheduled Non-Game Day → `rewards` is the partial per-wave OVERRIDE (merged over
 *     channel/global defaults at promotion), `rewardsSource === 'override'`.
 *   - active (either mode)   → `rewards` is the fully-resolved SNAPSHOT the live wave runs
 *     with, `rewardsSource === 'snapshot'`.
 *   - completed              → reward rules are not archived, so `rewards === null`. The tap
 *     target IS archived, so a finished wave still reports the one it ran with.
 *
 * The panel also fetches the channel's resolved defaults (GET /admin/fanwave/config) so an
 * override can be read against what it's layered on top of.
 */

const fmt = (d, pattern = 'MMM dd, yyyy · HH:mm') => (d ? format(new Date(d), pattern) : '—');

const modeLabel = (mode) => (mode === FANWAVE_MODE.GAME_DAY ? 'Game Day' : 'Non-Game Day');

const ELIGIBILITY_LABEL = {
  [BONUS_ELIGIBILITY.ALL]: 'all participants',
  [BONUS_ELIGIBILITY.MIN_TAPS]: 'fans who completed a cycle'
};
const CAP_BEHAVIOR_LABEL = {
  [CAP_BEHAVIOR.CONTINUE]: 'taps still count toward the goal',
  [CAP_BEHAVIOR.DISABLE]: 'tapping disabled'
};

const scheduleLabel = (wave) => {
  if (!wave) return '—';
  if (wave.mode === FANWAVE_MODE.GAME_DAY) {
    const g = wave.game;
    if (!g) return 'Match unavailable';
    const names = `${g.homeName || 'TBD'} vs ${g.awayName || 'TBD'}`;
    return g.date ? `${names} — ${fmt(g.date)}` : names;
  }
  return `${fmt(wave.startsAt, 'MMM dd, HH:mm')} → ${fmt(wave.endsAt, 'MMM dd, HH:mm')}`;
};

const StatCard = ({ label, value, highlight, title }) => (
  <Col
    noFlex
    pad="12px 14px"
    title={title}
    style={{
      flex: '1 1 120px',
      minWidth: '110px',
      background: highlight ? theme.SECONDARY_10 : theme.GREY_10,
      borderRadius: '11px',
      border: `1px solid ${highlight ? theme.SECONDARY_20 : theme.GREY_20}`
    }}
  >
    <Text
      marg="0"
      style={{
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: highlight ? theme.SECONDARY : theme.GREY_50
      }}
    >
      {label}
    </Text>
    <Text marg="4px 0 0" style={{ fontSize: '20px', fontWeight: 800, lineHeight: 1.1 }}>
      {value ?? '—'}
    </Text>
  </Col>
);

// A titled block inside the panel body.
const Block = ({ title, subtitle, children }) => (
  <Col
    noFlex
    wid="100%"
    pad="18px 20px"
    gap="12px"
    style={{
      background: '#fff',
      borderRadius: '14px',
      border: `1px solid ${theme.GREY_20}`,
      boxShadow: '0 1px 2px rgba(20, 28, 40, 0.04)'
    }}
  >
    <Col noFlex wid="100%" gap="2px">
      <Text h4 bold marg="0">
        {title}
      </Text>
      {subtitle && (
        <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: '12px', lineHeight: 1.45 }}>
          {subtitle}
        </Text>
      )}
    </Col>
    {children}
  </Col>
);

// "Label — value" line; only rendered when the value is present (so partial overrides show
// just the fields they actually set).
const Line = ({ label, value }) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <Row
      center
      between
      noFlex
      wid="100%"
      pad="4px 0"
      style={{ borderTop: `1px solid ${theme.GREY_10}` }}
    >
      <Text b2 col={theme.GREY_60} style={{ fontSize: '12px' }}>
        {label}
      </Text>
      <Text b2 bold marg="0" style={{ fontSize: '12.5px', textAlign: 'right' }}>
        {value}
      </Text>
    </Row>
  );
};

// A read-only rendering of a rewards object. `partial` (an override) shows only set fields;
// a full snapshot / channel default shows the resolved rules including their off state.
const RewardsSummary = ({ rewards, partial }) => {
  if (!rewards) return null;
  const has = (v) => v !== undefined && v !== null;
  const show = (v) => (partial ? has(v) : true);

  const participation =
    has(rewards.tapsPerCycle) && has(rewards.basePoints)
      ? `${rewards.tapsPerCycle} taps = ${rewards.basePoints} pts${
          rewards.allowMultipleCycles ? ' (repeatable)' : ''
        }`
      : undefined;

  return (
    <Col noFlex wid="100%" gap="0">
      <Line label="Participation" value={participation} />
      <Line label="Headline" value={rewards.headline} />
      <Line label="Goal message" value={rewards.goalMessage} />

      {show(rewards.progressiveEnabled) && (
        <Line
          label="Progressive rewards"
          value={
            rewards.progressiveEnabled ? `On · ${rewards.milestones?.length || 0} tiers` : 'Off'
          }
        />
      )}
      {rewards.progressiveEnabled &&
        (rewards.milestones || []).map((m, i) => (
          <Line key={i} label={`· ${m.taps} taps`} value={`${m.points} pts`} />
        ))}

      {show(rewards.communityBonusEnabled) && (
        <Line
          label="Community bonus"
          value={
            rewards.communityBonusEnabled
              ? `${rewards.communityBonusPoints ?? 0} pts → ${
                  ELIGIBILITY_LABEL[rewards.bonusEligibility] || 'all participants'
                }`
              : 'Off'
          }
        />
      )}

      {show(rewards.capEnabled) && (
        <Line
          label="Reward cap"
          value={
            rewards.capEnabled
              ? `${rewards.maxPoints ?? 0} pts max · ${
                  CAP_BEHAVIOR_LABEL[rewards.capBehavior] || 'taps still count toward the goal'
                }`
              : 'Off'
          }
        />
      )}

      {/* The other ceiling: how many times a fan may tap at all, as opposed to how many points
          those taps can earn. `0` is uncapped, never "0 taps allowed". */}
      {show(rewards.maxTapsPerUser) && (
        <Line
          label="Tap limit per fan"
          value={
            tapQuotaEnabled(rewards.maxTapsPerUser)
              ? `${rewards.maxTapsPerUser} taps per fan`
              : 'No tap limit'
          }
        />
      )}

      {show(rewards.rankRewardsEnabled) && (
        <Line
          label="Rank rewards"
          value={
            rewards.rankRewardsEnabled ? `On · ${rewards.rankRewards?.length || 0} bands` : 'Off'
          }
        />
      )}
      {rewards.rankRewardsEnabled &&
        (rewards.rankRewards || []).map((r, i) => (
          <Line
            key={i}
            label={`· #${r.fromRank}${r.toRank > r.fromRank ? `–#${r.toRank}` : ''}`}
            value={`${r.points} pts`}
          />
        ))}
    </Col>
  );
};

const WaveDetailPanel = ({ visible, wave, onClose }) => {
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);

  const teamId = wave?.teamId || wave?.team?._id;

  const fetchConfig = useCallback(async () => {
    if (!teamId) return;
    setLoadingConfig(true);
    const res = await Api.get(`/admin/fanwave/config?teamId=${teamId}`, null, { silent: true });
    if (res?.data) setConfig(res.data);
    setLoadingConfig(false);
  }, [teamId]);

  useEffect(() => {
    if (visible && teamId) fetchConfig();
  }, [visible, teamId, fetchConfig]);

  // Reset stale channel config when a different wave opens.
  useEffect(() => {
    setConfig(null);
  }, [teamId]);

  const isOverride = wave?.rewardsSource === REWARDS_SOURCE.OVERRIDE;
  const isSnapshot = wave?.rewardsSource === REWARDS_SOURCE.SNAPSHOT;
  // RESOLVED comes from a PUT /update response the list patched in — effective rules for a wave
  // that may not have started yet, so it is neither an override nor an activation snapshot.
  const isResolved = wave?.rewardsSource === REWARDS_SOURCE.RESOLVED;
  const configSubtitle = isSnapshot
    ? 'The fully-resolved rules this wave is running with (snapshotted at activation).'
    : isResolved
      ? 'The effective rules this wave will run with, as the API returned them after your last edit.'
      : isOverride
        ? 'Per-wave overrides — merged over the channel defaults below at activation.'
        : wave?.status === WAVE_STATUS.COMPLETED
          ? 'A completed wave archives its schedule and target, but not its reward rules — see the channel defaults below.'
          : 'This wave uses the channel defaults below — no per-wave overrides were set.';

  // A Game Day wave runs on one of FOUR schedules — match minutes, a wall-clock window, the
  // Half-Time default, or `mixed` (an edge on each clock; see `gameDayScheduleKind`). A run
  // archived before the schedule was persisted has none to show, and must say so rather than
  // inherit the fallback's guess.
  const unrecorded = scheduleUnrecorded(wave);
  const isCompleted = wave?.status === WAVE_STATUS.COMPLETED;
  const shape = unrecorded ? null : gameDayTimingShape(wave);
  const scheduleKindLabel = unrecorded
    ? 'Not recorded'
    : shape === WAVE_TIMING.MINUTES
      ? 'Match minutes'
      : shape === WAVE_TIMING.MIXED
        ? 'Match minute → a set time of day'
        : shape === WAVE_TIMING.HALF_TIME_UNTIL
          ? 'Half-Time → a set time of day'
          : shape === WAVE_TIMING.SECOND_HALF_UNTIL
            ? '2nd Half kickoff → a set time of day'
            : shape === WAVE_TIMING.HALF_TIME_INTERVAL
              ? 'The half-time break (Half-Time → 2nd Half kickoff)'
              : shape === WAVE_TIMING.HALF_TIME_FIXED
                ? `Fan Wave 1 (Half-Time → ${HALF_TIME_WAVE_MIN} minutes)`
                : shape === WAVE_TIMING.CLOCK_WINDOW
                  ? 'Clock window'
                  : shape === WAVE_TIMING.DEFAULT
                    ? 'Default (Half-Time → Full-Time + window)'
                    : undefined;
  // Both anchored shapes are answered here too, as they now are in the label above: neither
  // matched a branch, so the panel showed no schedule line AT ALL for a wave whose whole point is
  // where it opens and closes.
  const scheduleKindValue =
    shape === WAVE_TIMING.MINUTES
      ? matchMinutesLabel(wave)
      : shape === WAVE_TIMING.MIXED
        ? `${wave.startMinute}' → ${fmt(wave.endsAt, 'MMM dd, HH:mm')}`
        : shape === WAVE_TIMING.HALF_TIME_UNTIL
          ? `Half-Time → ${fmt(wave.endsAt, 'MMM dd, HH:mm')}`
          : shape === WAVE_TIMING.SECOND_HALF_UNTIL
            ? `2nd Half kickoff → ${fmt(wave.endsAt || wave.closeAnchor?.at, 'MMM dd, HH:mm')}`
            : shape === WAVE_TIMING.HALF_TIME_INTERVAL
              ? 'Half-Time → 2nd Half kickoff (both read off the live match)'
              : shape === WAVE_TIMING.HALF_TIME_FIXED
                ? `Half-Time → ${HALF_TIME_WAVE_MIN} minutes after it opens, or the 2nd Half kickoff if sooner`
                : shape === WAVE_TIMING.CLOCK_WINDOW
                  ? `${fmt(wave.startsAt, 'MMM dd, HH:mm')} → ${fmt(wave.endsAt, 'MMM dd, HH:mm')}`
                  : undefined;
  const hasScheduleLines = !!scheduleKindLabel;

  return (
    <Sidepanel visible={visible} wid={720} pad="0" closeModal={onClose}>
      <PanelHeader text="FanWave configuration" onClose={onClose} />
      <Col wid="100%" pad="20px 24px 40px" gap="16px">
        {/* Context */}
        <Col noFlex wid="100%" gap="6px">
          <Row center start noFlex gap="8px" style={{ flexWrap: 'wrap' }}>
            <Text b2 bold marg="0">
              {wave?.team?.name || wave?.team?.username || 'Channel'}
            </Text>
            <Badge variant={wave?.mode === FANWAVE_MODE.GAME_DAY ? 'locked' : 'neutral'}>
              {modeLabel(wave?.mode)}
            </Badge>
            <Badge variant={STATUS_BADGE_VARIANT[wave?.status] || 'neutral'} dot>
              {wave?.status}
            </Badge>
          </Row>
          <Text c2 col={theme.GREY_60} marg="0">
            {scheduleLabel(wave)}
          </Text>
        </Col>

        {/* Headline numbers */}
        <Row start gap="10px" wid="100%" style={{ flexWrap: 'wrap' }}>
          <StatCard
            label="Tap target"
            value={wave?.tapTarget ?? '—'}
            title={missingTapTargetReason(wave)}
            highlight
          />
          <StatCard label="Total taps" value={wave?.totalHits ?? 0} />
          <StatCard label="Fans" value={wave?.totalParticipants ?? 0} />
        </Row>

        {/* Per-wave configuration */}
        <Block title="Wave configuration" subtitle={configSubtitle}>
          {scheduleKindLabel && <Line label="Schedule" value={scheduleKindLabel} />}
          {scheduleKindValue && (
            <Line
              label={
                shape === WAVE_TIMING.MINUTES
                  ? 'Match minutes'
                  : isCompleted
                    ? 'Ran'
                    : 'Appears → disappears'
              }
              value={scheduleKindValue}
            />
          )}
          <Line label="Auto restart" value={wave?.autoRestart ? 'On' : 'Off'} />
          {wave?.autoRestart && (
            <>
              <Line
                label="Max restarts"
                value={wave.maxRestarts != null ? wave.maxRestarts : 'Until the wave ends'}
              />
              <Line
                label="Stops restarting when under"
                value={
                  wave.minRemainingMinutes != null
                    ? `${wave.minRemainingMinutes} min left`
                    : 'No limit'
                }
              />
            </>
          )}
          {(wave?.autoRestart || wave?.restartIndex > 0) && (
            <Line
              label="Session"
              value={wave.restartIndex > 0 ? `Restart ${wave.restartIndex}` : 'First session'}
            />
          )}
          {wave?.rewards ? (
            <RewardsSummary rewards={wave.rewards} partial={isOverride} />
          ) : (
            !hasScheduleLines && (
              <Text c1 col={theme.GREY_50} marg="0" style={{ fontSize: '12.5px' }}>
                No per-wave configuration recorded.
              </Text>
            )
          )}
        </Block>

        {/* Channel defaults */}
        <Block
          title="Channel defaults"
          subtitle="The resolved target and reward rules currently in effect for this channel."
        >
          {loadingConfig ? (
            <Row centerAll pad="16px 0">
              <Spinner2 />
            </Row>
          ) : config ? (
            <>
              <ResolvedTarget
                tapTarget={config.tapTarget}
                source={config.source || {}}
                locked={config.locked}
              />
              <Line
                label="Post-FT window"
                value={
                  config.postFtMinutes != null
                    ? minutesToHoursLabel(config.postFtMinutes) || `${config.postFtMinutes} min`
                    : undefined
                }
              />
              <RewardsSummary rewards={config.rewards} partial={false} />
            </>
          ) : (
            <Text c1 col={theme.GREY_50} marg="0" style={{ fontSize: '12.5px' }}>
              Channel defaults unavailable.
            </Text>
          )}
        </Block>
      </Col>
    </Sidepanel>
  );
};

export default WaveDetailPanel;
